import { FormField } from '@/global/components/Form'
import IconeAjuda from '@/global/components/IconeAjuda'
import { SimpleTabs } from '@/global/components/Tabs'
import { useAuth } from '@/hooks'
import { cn } from '@/lib/utils'
import {
    Ocorrencia,
    ocorrencia,
    ocorrenciaTipoToPermission,
    PerfilPermissao,
} from '@/service/api'
import {
    CategoriaEnum,
    defineAbilitiesFor,
    groupAbilitiesByModule,
    separateWords,
} from '@acme/auth'
import { UserAbilityTuple } from '@acme/auth/types'
import { insensitive, onlyDefined, onlyUnique } from '@acme/common'
import { Checkbox, Grid, TextField } from '@mui/material'
import { SearchIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

export default function FormPerfilPermissao() {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormField name="nome" label="Nome do perfil" required />
            </Grid>
            <Grid item xs={12}>
                <TablePermissoes />
            </Grid>
        </Grid>
    )
}

type MappedAbilities = {
    subject: string
    abilities: UserAbilityTuple[0]
}

function TablePermissoes() {
    const { permissions: myPermissions, decoded } = useAuth()
    const { data = [] } = ocorrencia.useFetch({
            refetchOnWindowFocus: false,
        }),
        ocorrenciasOrganizadas = data.sort((a, b) =>
            a.descricao.localeCompare(b.descricao),
        )
    const [ocorrencias, setOcorrencias] = useState<Ocorrencia[]>([])

    useEffect(() => {
        setOcorrencias(ocorrenciasOrganizadas)
    }, [data])

    // Cache da ability do supervisor para evitar recriação desnecessária
    const supervisorAbility = useMemo(
        () =>
            defineAbilitiesFor({
                categoriaId: CategoriaEnum.Supervisor,
                isAtivo: true,
                modulos: decoded.modulos,
            }),
        [decoded.modulos],
    )

    const subjects = myPermissions.rules
        .map(it => it.subject)
        .filter(onlyUnique)

    const mapped = subjects
        .map((subject: string) => {
            const abilities = myPermissions.rules
                .filter(it => it.subject === subject)
                .flatMap(it => it.action)
                .filter(onlyUnique)
                .filter(it => supervisorAbility.can(it, subject))

            if (abilities.length)
                return {
                    subject,
                    abilities,
                }
        })
        .filter(onlyDefined)

    const [rows, setRows] = useState<MappedAbilities[]>(mapped)

    return (
        <SimpleTabs
            tabs={[
                {
                    value: 'rotas',
                    label: 'Permissões de rotas',
                    content: (
                        <div
                            className="overflow-y-auto h-96 rounded-sm scrollbar-medium border-2 border-card"
                            style={{ scrollbarGutter: 'stable' }}
                        >
                            <table className="w-full caption-bottom text-sm border-collapse table-fixed">
                                <thead className="sticky top-0 z-2 bg-card">
                                    <tr
                                        className={cn(
                                            '*:px-2 *:py-3',
                                            '*:[&_span]:flex *:[&_span]:w-full',
                                            '*:[&_span]:items-center *:[&_span]:justify-start',
                                        )}
                                    >
                                        <th className="w-auto">
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                autoComplete="off"
                                                InputProps={{
                                                    sx: {
                                                        opacity: 0.5,
                                                        height: 30,
                                                        width: '100%',
                                                    },
                                                    startAdornment: (
                                                        <SearchIcon className="mr-2" />
                                                    ),
                                                }}
                                                onChange={event => {
                                                    const value = insensitive(
                                                        event.target.value,
                                                    )
                                                    if (!value) setRows(mapped)
                                                    else
                                                        setRows(
                                                            mapped.filter(row =>
                                                                insensitive(
                                                                    row.subject,
                                                                ).includes(
                                                                    value,
                                                                ),
                                                            ),
                                                        )
                                                }}
                                            />
                                        </th>
                                        <th className="w-25">
                                            <span>
                                                <CheckBoxAll
                                                    allPermissions={mapped}
                                                    rows={rows}
                                                    type="read"
                                                />
                                                Ler
                                            </span>
                                        </th>
                                        <th className="w-25">
                                            <span>
                                                <CheckBoxAll
                                                    allPermissions={mapped}
                                                    rows={rows}
                                                    type="create"
                                                />
                                                Criar
                                            </span>
                                        </th>
                                        <th className="w-25">
                                            <span>
                                                <CheckBoxAll
                                                    allPermissions={mapped}
                                                    rows={rows}
                                                    type="update"
                                                />
                                                Atualizar
                                            </span>
                                        </th>
                                        <th className="w-25">
                                            <span>
                                                <CheckBoxAll
                                                    allPermissions={mapped}
                                                    rows={rows}
                                                    type="delete"
                                                />
                                                Excluir
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="min-h-full">
                                    {Object.entries(
                                        groupAbilitiesByModule(rows),
                                    )
                                        .map(entry =>
                                            filterPermissions(
                                                entry,
                                                supervisorAbility,
                                            ),
                                        )
                                        .flatMap(([modulo, rows]) =>
                                            rows.length
                                                ? [
                                                      <tr
                                                          key={modulo}
                                                          className="bg-card/80"
                                                      >
                                                          <td
                                                              colSpan={5}
                                                              className="px-2 font-semibold text-center h-8"
                                                          >
                                                              {separateWords(
                                                                  modulo,
                                                              )}
                                                          </td>
                                                      </tr>,
                                                      ...rows.map(
                                                          (row, index) => (
                                                              <Row
                                                                  key={`${modulo}-${index}`}
                                                                  subject={
                                                                      row.subject
                                                                  }
                                                                  abilities={
                                                                      row.abilities
                                                                  }
                                                                  modulo={
                                                                      modulo
                                                                  }
                                                              />
                                                          ),
                                                      ),
                                                  ]
                                                : [],
                                        )}
                                </tbody>
                            </table>
                        </div>
                    ),
                },
                {
                    value: 'ocorrencias',
                    label: 'Permissões de ocorrências',
                    content: (
                        <div
                            className="overflow-y-auto h-96 rounded-sm scrollbar-medium border-2 border-card"
                            style={{ scrollbarGutter: 'stable' }}
                        >
                            <table className="w-full caption-bottom text-sm border-collapse table-fixed">
                                <thead className="sticky top-0 z-2 bg-card">
                                    <tr
                                        className={cn(
                                            '*:px-2 *:py-3',
                                            '*:[&_span]:flex *:[&_span]:w-full',
                                            '*:[&_span]:items-center *:[&_span]:justify-start',
                                        )}
                                    >
                                        <th className="w-auto">
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                autoComplete="off"
                                                InputProps={{
                                                    sx: {
                                                        opacity: 0.5,
                                                        height: 30,
                                                        width: '100%',
                                                    },
                                                    startAdornment: (
                                                        <SearchIcon className="mr-2" />
                                                    ),
                                                }}
                                                onChange={event => {
                                                    const value = insensitive(
                                                        event.target.value,
                                                    )
                                                    if (!value)
                                                        setOcorrencias(
                                                            ocorrenciasOrganizadas,
                                                        )
                                                    else
                                                        setOcorrencias(
                                                            ocorrenciasOrganizadas.filter(
                                                                ocorr =>
                                                                    insensitive(
                                                                        ocorr.descricao,
                                                                    ).includes(
                                                                        value,
                                                                    ),
                                                            ),
                                                        )
                                                }}
                                            />
                                        </th>
                                        <th className="w-fit text-start inline-flex items-center gap-2">
                                            Tipo
                                            <IconeAjuda>
                                                Defina permissão para o tipo de
                                                ocorrência para que o usuário
                                                consiga utilizá-la.
                                                <br />
                                                <br />
                                                Exemplo: para permitir que o
                                                usuário crie ocorrências do tipo
                                                "Afastamentos", é necessário que
                                                ele tenha a permissão de "criar
                                                Afastamento" ativada.
                                            </IconeAjuda>
                                        </th>
                                        <th className="w-auto">
                                            <span>
                                                <CheckboxGerenciarAll />
                                                Pode gerenciar
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="min-h-full">
                                    {ocorrencias.map(
                                        ({ id, descricao, tipo }) => (
                                            <tr
                                                className="even:bg-card/30 *:px-2 *:max-w-full *:truncate *:text-nowrap"
                                                key={id}
                                            >
                                                <td title={descricao}>
                                                    {descricao}
                                                </td>
                                                <td>
                                                    {ocorrenciaTipoToPermission(
                                                        tipo,
                                                    )}
                                                </td>
                                                <td>
                                                    <CheckboxGerenciar
                                                        {...{ id, tipo }}
                                                    />
                                                </td>
                                            </tr>
                                        ),
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ),
                },
            ]}
        />
    )
}

function CheckboxGerenciarAll() {
    const { data: ocorrencias = [] } = ocorrencia.useFetch()
    const { watch, setValue } = useFormContext<
        PerfilPermissao & {
            ExcecoesPermissaoOcorrencia: {
                ocorrenciaId: number
            }[]
        }
    >()
    const permissoesOcorrencias = watch('ExcecoesPermissaoOcorrencia', [])

    const state = (() => {
        const count = permissoesOcorrencias.length

        if (count === 0) return 'checked'
        if (
            ocorrencias.every(({ id }) =>
                permissoesOcorrencias.map(it => it.ocorrenciaId).includes(id),
            )
        )
            return 'unchecked'
        return 'indeterminate'
    })()

    return (
        <Checkbox
            checked={state === 'checked'}
            indeterminate={state === 'indeterminate'}
            onChange={event => {
                const checked = event.target.checked
                let newPermissoes = ocorrencias.map(it => ({
                    ocorrenciaId: it.id,
                }))
                if (checked) newPermissoes = []

                setValue('ExcecoesPermissaoOcorrencia', newPermissoes)
            }}
            className="max-w-10 aspect-1/1"
        />
    )
}

function CheckboxGerenciar({ id, tipo }: Pick<Ocorrencia, 'id' | 'tipo'>) {
    const { watch, setValue } = useFormContext<
        PerfilPermissao & {
            ExcecoesPermissaoOcorrencia: {
                ocorrenciaId: number
            }[]
            appAbilities: UserAbilityTuple[]
        }
    >()
    const permissoesOcorrencias = watch('ExcecoesPermissaoOcorrencia', []),
        appAbilities = watch('appAbilities', [])

    const isDisabled = !appAbilities.some(
        ab =>
            ab[0].includes('create') &&
            String(insensitive(ab[1])).trim().replace(/s$/i, '') ===
                insensitive(tipo).trim(),
    )

    return (
        <Checkbox
            checked={
                !isDisabled &&
                !permissoesOcorrencias.some(it => it.ocorrenciaId === id)
            }
            onChange={event => {
                const checked = event.target.checked
                let newPermissoes = structuredClone(permissoesOcorrencias)
                if (checked) {
                    if (newPermissoes.map(it => it.ocorrenciaId).includes(id)) {
                        newPermissoes = newPermissoes.filter(
                            it => it.ocorrenciaId !== id,
                        )
                    }
                } else {
                    newPermissoes.push({
                        ocorrenciaId: id,
                    })
                }
                setValue('ExcecoesPermissaoOcorrencia', newPermissoes)
            }}
            disabled={isDisabled}
            className={cn('max-w-10 aspect-1/1', {
                'opacity-50': isDisabled,
            })}
        />
    )
}

type FilterTuple = [string, MappedAbilities[]]
function filterPermissions(
    [group, rows]: FilterTuple,
    supervisorAbility: any,
): FilterTuple {
    // Filtrar permissões que não estão disponíveis para o perfil Supervisor
    const filteredRows: MappedAbilities[] = []

    if (group === 'Ajuda') return [group, filteredRows]

    for (const row of rows) {
        // Filtrar abilities que não são permitidas para este subject
        const allowedAbilities = row.abilities.filter(abilityType =>
            supervisorAbility.can(abilityType, row.subject),
        )

        // Só incluir se ainda tem abilities após a filtragem
        if (allowedAbilities.length > 0) {
            filteredRows.push({
                subject: row.subject,
                abilities: allowedAbilities,
            })
        }
    }

    return [group, filteredRows]
}

function Row(row: MappedAbilities & { modulo: string }) {
    const { watch, setValue } = useFormContext<
        PerfilPermissao & { appAbilities: UserAbilityTuple[] }
    >()
    const appAbilities = watch('appAbilities', [])

    return (
        <tr className="even:bg-card/30">
            <td className="px-2 truncate" title={separateWords(row.subject)}>
                {separateWords(row.subject)}
            </td>
            {Array.from({ length: 4 }).map((_, i) => {
                const ability = (
                        ['read', 'create', 'update', 'delete'] as const
                    )[i],
                    abilityExists = row.abilities.includes(ability)

                const isChecked = appAbilities.some(
                    ab =>
                        ab.at(1) === row.subject && ab.at(0)?.includes(ability),
                )

                return (
                    <td key={i} className="p-2 text-start">
                        {!abilityExists ? (
                            <></>
                        ) : (
                            <Checkbox
                                checked={isChecked}
                                onChange={v => {
                                    const newAbilities = v.target.checked
                                        ? addAbility(
                                              appAbilities,
                                              ability,
                                              row.subject,
                                          )
                                        : removeAbility(
                                              appAbilities,
                                              ability,
                                              row.subject,
                                          )
                                    setValue('appAbilities', newAbilities)
                                }}
                            />
                        )}
                    </td>
                )
            })}
        </tr>
    )
}

function CheckBoxAll({
    rows,
    type,
    allPermissions,
}: {
    allPermissions: MappedAbilities[]
    rows: MappedAbilities[]
    type: UserAbilityTuple[0][number]
}) {
    const { watch, setValue } = useFormContext<
        PerfilPermissao & { appAbilities: UserAbilityTuple[] }
    >()
    const appAbilities = watch('appAbilities', [])

    const state = (() => {
        const count = appAbilities.filter(ab => ab[0].includes(type)).length

        if (count === 0) return 'unchecked'
        if (
            count >=
            allPermissions.filter(it => it.abilities.includes(type)).length
        )
            return 'checked'
        return 'indeterminate'
    })()

    return (
        <Checkbox
            checked={state === 'checked'}
            indeterminate={state === 'indeterminate'}
            onChange={event => {
                const checked = event.target.checked
                let newAbilities = structuredClone(appAbilities)
                for (const row of Object.values(
                    rows.filter(r => r.abilities.includes(type)),
                )) {
                    if (checked) {
                        newAbilities = addAbility(
                            newAbilities,
                            type,
                            row.subject,
                        )
                    } else {
                        newAbilities = removeAbility(
                            newAbilities,
                            type,
                            row.subject,
                        )
                    }
                }
                setValue('appAbilities', newAbilities)
            }}
            className="max-w-10 aspect-1/1"
        />
    )
}

function addAbility(
    prev: UserAbilityTuple[],
    ability: UserAbilityTuple[0][number],
    subject: UserAbilityTuple[1],
): UserAbilityTuple[] {
    const abilities = prev.find(ab => ab.at(1) === subject)
    if (abilities?.at(0)?.length) {
        const modifiedAbility = [...abilities[0], ability].filter(onlyUnique)
        return prev.map(ab =>
            ab[1] === subject ? [modifiedAbility, ab[1]] : ab,
        )
    }

    return prev.concat([[[ability], subject]])
}

function removeAbility(
    prev: UserAbilityTuple[],
    ability: UserAbilityTuple[0][number],
    subject: UserAbilityTuple[1],
): UserAbilityTuple[] {
    const abilities = prev.find(ab => ab.at(1) === subject)
    if (abilities?.at(0)?.length) {
        const modifiedAbility = abilities[0].filter(ab => ab !== ability)
        if (modifiedAbility.length) {
            return prev.map(ab =>
                ab[1] === subject ? [modifiedAbility, ab[1]] : ab,
            )
        }
        return prev.filter(ab => ab[1] !== subject)
    }
    return prev
}
